/**
 * Talas and the konnakol parser. Pure — no audio, no DOM.
 * Transferred from Chirp/flameback.html: the TALAS table, KONNAKOL_MAP and
 * parseKonnakol. Typed here, and extended with phrase fitting (Flameback's
 * layKonnakol gati auto-fit, lifted out of its DOM handler).
 */

import { parseKonnakol as coreParseKonnakol } from '../audio/vendor/av-core.mjs';

export type StrokeId = 'tha' | 'ka' | 'dhin' | 'thom' | 'nam' | 'chapu' | 'dheem' | 'tham';

export interface Tala {
  readonly label: string;
  readonly beats: number;
  readonly angas: readonly number[];
  readonly tip: string;
}

export const TALAS = {
  adi: { label: 'ADI', beats: 8, angas: [4, 2, 2], tip: 'Chatusra-jati triputa — a laghu of 4 and two drutams.' },
  rupaka: { label: 'RUPAKA', beats: 6, angas: [2, 4], tip: 'Drutam + chatusra laghu = 6.' },
  misra: { label: 'MISRA CHAPU', beats: 7, angas: [3, 2, 2], tip: '3+2+2 — seven that never stumbles.' },
  khanda: { label: 'KHANDA CHAPU', beats: 5, angas: [2, 3], tip: '2+3 — five with a lean.' },
  free: { label: 'FREE', beats: 4, angas: [4], tip: 'Four plain beats — x4 makes the 16-step machine.' },
} as const satisfies Record<string, Tala>;

export type TalaId = keyof typeof TALAS;
export const TALA_IDS = Object.keys(TALAS) as TalaId[];

/** One school's hands — a drill, not a decree. Correct it by ear. */
export const KONNAKOL_MAP: Record<string, StrokeId> = {
  ta: 'tha', tha: 'tha', thaa: 'tha', thi: 'tha',
  ka: 'ka', ki: 'ka', ku: 'ka',
  dhi: 'dhin', di: 'dhin', dhin: 'dhin', din: 'dhin', dhim: 'dhin',
  mi: 'nam', na: 'nam', nam: 'nam', nan: 'nam', nom: 'nam',
  cha: 'chapu', chapu: 'chapu', chi: 'chapu',
  thom: 'thom', dhom: 'thom', dom: 'thom', gu: 'thom', num: 'thom', ghum: 'thom',
  dheem: 'dheem', dhee: 'dheem',
  tham: 'tham',
};

export interface KonnakolParse {
  /** One entry per spoken syllable. `null` is a rest (the karvai). */
  steps: (StrokeId | null)[];
  /** Raw tokens that mapped to nothing — laid as rests, but flagged honestly. */
  unknown: string[];
}

/**
 * Text → strokes. SINCE AV-CORE PHASE 3 (flyway intent 2026-10-04-av-core-inline)
 * the parser IS Chirp's single-source av-core parseKonnakol
 * (../audio/vendor/av-core.mjs), run over THIS module's KONNAKOL_MAP — the
 * instrument's own subset (no gumup/dhimi rows), so it stays local. fitPhrase,
 * layPhrase and the TALAS table have no av-core counterpart and stay local.
 */
export function parseKonnakol(text: string): KonnakolParse {
  return coreParseKonnakol(text, KONNAKOL_MAP) as KonnakolParse;
}

export interface PhraseFit {
  /** Steps per beat the phrase was fitted to (the gati). */
  spb: number;
  /** True when the syllable count divided cleanly into one avartanam. */
  fitted: boolean;
  /** Total grid width at the chosen gati. */
  gridSteps: number;
  /** Syllables past one avartanam, which are dropped. */
  overflow: number;
}

/**
 * Auto-fit the gati: one syllable per step for exactly one avartanam, when the
 * count is a clean 2/3/4 per beat. Otherwise keep the current gati and say so.
 */
export function fitPhrase(syllables: number, tala: TalaId, currentSpb: number): PhraseFit {
  const beats = TALAS[tala].beats;
  for (const g of [2, 3, 4]) {
    if (syllables === beats * g) {
      return { spb: g, fitted: true, gridSteps: Math.min(beats * g, 32), overflow: 0 };
    }
  }
  const gridSteps = Math.min(beats * currentSpb, 32);
  return { spb: currentSpb, fitted: false, gridSteps, overflow: Math.max(0, syllables - gridSteps) };
}

export type Grid = Record<StrokeId, boolean[]>;

export const emptyGrid = (steps: number): Grid => ({
  tha: new Array(steps).fill(false), ka: new Array(steps).fill(false),
  dhin: new Array(steps).fill(false), thom: new Array(steps).fill(false),
  nam: new Array(steps).fill(false), chapu: new Array(steps).fill(false),
  dheem: new Array(steps).fill(false), tham: new Array(steps).fill(false),
});

/** A phrase REPLACES the grid — konnakol is a phrase, not a lattice. */
export function layPhrase(steps: (StrokeId | null)[], gridSteps: number): { grid: Grid; laid: number } {
  const grid = emptyGrid(gridSteps);
  let laid = 0;
  for (let s = 0; s < Math.min(steps.length, gridSteps); s++) {
    const st = steps[s];
    if (st) { grid[st][s] = true; laid++; }
  }
  return { grid, laid };
}

export const KONNAKOL_EXAMPLES = [
  { label: 'CHATUSRA', text: 'tha ka dhi mi' },
  { label: 'TISRA', text: 'tha ki ta' },
  { label: 'KHANDA', text: 'tha ka tha ki ta' },
  { label: 'MISRA', text: 'tha ki ta tha ka dhi mi' },
  { label: 'SARVALAGHU', text: 'tha ka dhi mi ki ta tha ka' },
] as const;

/** Display order for the stroke grid: treble strokes first, then the bass head. */
export const STROKE_ORDER: StrokeId[] = ['tha', 'nam', 'dhin', 'chapu', 'ka', 'thom', 'dheem', 'tham'];
